import {patchState, signalStore, withComputed, withLinkedState, withMethods, withState} from '@ngrx/signals';
import {withDevtools} from '@angular-architects/ngrx-toolkit';
import {computed, inject} from '@angular/core';
import {ApiService} from '../../../shared/services/api.service';
import {TYPE} from '../../../shared/types/types';
import {Observable, take} from 'rxjs';
import {KnowledgebaseSubPageResource} from '../../../shared/types/knowledgebase-types/knowledgebaseSubPageResource';
import {KnowledgebasePageResource} from '../../../shared/types/knowledgebase-types/knowledgebasePageResource';
import {KnowledgebaseContentResource} from '../../../shared/types/knowledgebase-types/knowledgebaseContentResource';
import {KnowledgebasePageUpdateRequest} from '../../../shared/types/knowledgebase-types/knowledgebasePageUpdateRequest';
import {KnowledgebaseContentUpdateRequest} from '../../../shared/types/knowledgebase-types/knowledgebaseContentUpdateRequest';
import {KnowledgebaseMoveDirection} from '../../../shared/types/knowledgebase-types/knowledgebaseMoveDirection';
import {KnowledgebasePageAdminResource} from '../../../shared/types/knowledgebase-types/knowledgebasePageAdminResource';
import {UserMinimalResource} from '../../../shared/types/knowledgebase-types/userMinimalResource';
import {Router} from '@angular/router';
import {KnowledgebasePageTreeResource} from '../../../shared/types/knowledgebase-types/knowledgebasePageTreeResource';
import {FileStorageResource} from '../../../shared/types/settings-types/fileStorageResource';
import {KnowledgebaseContentListResource} from '../../../shared/types/knowledgebase-types/knowledgebaseContentListResource';
import {DocumentService} from '../../../shared/services/document.service';


export interface KnowledgebaseState {
  filter: {
    query: string,
    type: TYPE.KnowledgebaseFilterType,
  },
  categoryOrder: TYPE.KnowledgebaseCategoryOrder,
  fileView: TYPE.KnowledgebaseFileViewType,
  categoryContentOrder: TYPE.KnowledgebaseCategoryContentOrder,
  categoryContentFilter: TYPE.KnowledgebaseCategoryContentFilter,
  routeData: {
    categoryId: number | null,
    articleId: number | null,
  },
  rootPages: KnowledgebaseSubPageResource[];
  rootPagination: {
    limit: number;
    maxReached: boolean;
  };
  pages: KnowledgebasePageResource[];
  pagePagination: Map<number, {
    limit: number;
    maxReached: boolean;
  }>;
  articles: Map<number, KnowledgebaseContentListResource[]>;
  articlesPagination: Map<number, {
    skip: number;
    limit: number;
    maxReached: boolean;
  }>;
  files: Map<number, FileStorageResource[]>;
  filesPagination: Map<number, {
    skip: number;
    limit: number;
    maxReached: boolean;
  }>;
  categoriesLoading: boolean;
  categoryTree: {
    entries: KnowledgebasePageTreeResource[];
    searchString: string;
  }
}

const initialState: KnowledgebaseState = {
  filter: {
    query: '',
    type: TYPE.KnowledgebaseFilterType.CATEGORY,
  },
  categoryOrder: TYPE.KnowledgebaseCategoryOrder.PREDEFINED,
  fileView: TYPE.KnowledgebaseFileViewType.LIST,
  categoryContentOrder: TYPE.KnowledgebaseCategoryContentOrder.PREDEFINED,
  categoryContentFilter: TYPE.KnowledgebaseCategoryContentFilter.ARTICLES_ONLY,
  routeData: {
    categoryId: null,
    articleId: null,
  },
  rootPages: [],
  rootPagination: {
    limit: 20,
    maxReached: false,
  },
  pages: [],
  pagePagination: new Map<number, { limit: number,maxReached: false }>(),
  articles: new Map<number, KnowledgebaseContentListResource[]>(),
  articlesPagination: new Map<number, { skip: number, limit: number,maxReached: false }>(),
  files: new Map<number, FileStorageResource[]>(),
  filesPagination: new Map<number, { skip: number, limit: number,maxReached: false }>(),
  categoriesLoading: false,
  categoryTree: {
    entries: [],
    searchString: '',
  },
};

export const KnowledgebaseStore = signalStore(
  {providedIn: 'root'},
  withState(initialState),
  withDevtools('KnowledgebaseStore'),
  withLinkedState(({articles, files, pages, routeData, rootPagination, articlesPagination}) => ({
    currentArticles: (): KnowledgebaseContentListResource[] => {
      const idToCompare = routeData.categoryId() === null || routeData.categoryId() === 0 ? 0 : routeData.categoryId();
      return articles().get(Number(idToCompare)) ?? [];
    },
    currentFiles: (): FileStorageResource[] => {
      const idToCompare = routeData.categoryId() === null || routeData.categoryId() === 0 ? 0 : routeData.categoryId();
      return files().get(Number(idToCompare)) ?? [];
    },
    currentCategory: (): KnowledgebasePageResource | undefined => {
      return pages().find((cat) => cat.id === routeData.categoryId());
    },
    hideArticleEditorIcon: (): boolean => {
      const page = pages().find((cat) => cat.id === routeData.categoryId())
      if(page) {
        return page.hide_editor_icon;
      } else {
        return false;
      }
    },
    hasParentCategory: (): boolean => routeData.categoryId() !== null && routeData.categoryId() !== 0,
    hasMoreDataToLoad: (): boolean => {
      const hasParent = routeData.categoryId() !== null && routeData.categoryId() !== 0;
      if (hasParent) {
        const categoryId = Number(routeData.categoryId());
        const pagination = articlesPagination().get(categoryId);
        return pagination ? !pagination.maxReached : true;
      } else {
        return !rootPagination().maxReached;
      }
    },
  })),
  withMethods((store) => {
    const api: ApiService = inject(ApiService);
    const router: Router = inject(Router);
    const documentService: DocumentService = inject(DocumentService);

    return{
      resetStore(): void {
        patchState(store, {
          ...initialState,
          pagePagination: new Map<number, { limit: number, maxReached: boolean }>(),
          articles: new Map<number, KnowledgebaseContentListResource[]>(),
          articlesPagination: new Map<number, { skip: number, limit: number, maxReached: boolean }>(),
          files: new Map<number, FileStorageResource[]>(),
          filesPagination: new Map<number, { skip: number, limit: number, maxReached: boolean }>(),
        });
      },
      updateFilterQuery(query: string): void {
        patchState(store, (state: KnowledgebaseState) => 
          ({
            ...state,
            filter: {
              ...state.filter,
              query
            }
          })
        )
        this.resetPages();
        this.resetRootPages();
        this.loadCategories();
        this.resetArticles();
        this.resetFiles();
        this.loadArticlesForPage();
        this.loadFilesForPage();
      },
      updateFilterType(type: TYPE.KnowledgebaseFilterType): void {
        patchState(store, (state: KnowledgebaseState) => 
          ({
            ...state,
            filter: {
              ...state.filter,
              type
            }
          })
        );
        if(store.filter.query() !== '') {
          this.resetPages();
          this.resetRootPages();
          this.loadCategories();
          this.resetArticles();
          this.resetFiles();
          this.loadArticlesForPage();
          this.loadFilesForPage();
        }
      },
      updateCategoryOrder(categoryOrder: TYPE.KnowledgebaseCategoryOrder): void {
        patchState(store, (state: KnowledgebaseState) => 
          ({
            ...state,
            categoryOrder,
            rootPagination: initialState.rootPagination,
            pagePagination: initialState.pagePagination,
          })
        )
        this.reloadCategories();
      },
      updateCategoryContentOrder(categoryContentOrder: TYPE.KnowledgebaseCategoryContentOrder): void {
        if(categoryContentOrder !== store.categoryContentOrder()) {
          patchState(store, (state: KnowledgebaseState) => 
            ({
              ...state,
              categoryContentOrder
            })
          );
          this.resetArticles();
          this.resetFiles();
          this.loadArticlesForPage();
          this.loadFilesForPage();
        }
      },
      updateCategoryContentFilter(categoryContentFilter: TYPE.KnowledgebaseCategoryContentFilter): void {
        patchState(store, (state: KnowledgebaseState) => 
          ({
            ...state,
            categoryContentFilter
          })
        )
      },
      resetCategoryContentSettings(): void {
        patchState(store, (state: KnowledgebaseState) => 
          ({
            ...state,
            categoryContentOrder: TYPE.KnowledgebaseCategoryContentOrder.PREDEFINED,
            categoryContentFilter: TYPE.KnowledgebaseCategoryContentFilter.ARTICLES_ONLY
          })
        )
      },
      updateRouteData(categoryId: number | null, articleId: number | null): void {
        if(categoryId !== store.routeData.categoryId() || articleId !== store.routeData.articleId()) {
          const shouldLoadCategory: boolean = categoryId !== store.routeData.categoryId();
          patchState(store, (state: KnowledgebaseState) => 
            ({
              ...state,
              routeData: {
                categoryId,
                articleId
              },
            })
          )
          if(shouldLoadCategory) {
            this.resetRootPages();
            this.resetPages();
            this.resetArticles();
            this.resetFiles();
            this.loadCategories();
            this.loadArticlesForPage();
            this.loadFilesForPage();
          }
        }
      },
      updatefileView(fileView: TYPE.KnowledgebaseFileViewType): void {
        patchState(store, (state: KnowledgebaseState) => 
          ({
            ...state,
            fileView
          })
        );
      },
      scrollEndLoader(): void {
        if(store.hasParentCategory()) {
          if(store.categoryContentFilter() === TYPE.KnowledgebaseCategoryContentFilter.ARTICLES_ONLY) {
            this.loadArticlesForPage();
          } else {
            this.loadFilesForPage();
          }
        } else {
          this.loadRootPage();
        }
      },
      reloadCategories(): void {
        this.resetRootPages();
        this.resetPages();
        this.resetArticles();
        this.resetFiles();
        this.loadCategories();
        this.loadArticlesForPage();
        this.loadFilesForPage();
      },
      loadCategories(): void {
        patchState(store, (state: KnowledgebaseState) => ({
          ...state,
          categoriesLoading: true,
        }));
        if(store.routeData.categoryId() === null || store.routeData.categoryId() === 0) {
          this.loadRootPage();
        } else {
          this.loadPage(store.routeData.categoryId() as number);
        }
        this.resetCategoryTree();
      },
      loadRootPage(): void {
        if(store.rootPagination.maxReached() === false) {
          api.getKnowledgebaseRootPages(
            store.categoryOrder(),
            store.filter.type() === TYPE.KnowledgebaseFilterType.CATEGORY ? store.filter.query() :'',
            store.rootPagination.limit(),
            store.rootPages().length > 0 ? store.rootPages()[store.rootPages().length - 1].id : null)
          .pipe(take(1))
          .subscribe({
            next: (response) => {
              const rootPages = response.data;
              const moreEntries = response.moreEntries;
              patchState(store, (state: KnowledgebaseState) => {
                // Merge existing and loaded root pages, remove duplicates by id
                const mergedPages = [
                ...state.rootPages,
                ...rootPages
                ];
                const uniquePages = Array.from(
                new Map(mergedPages.map(page => [page.id, page])).values()
                );
                return {
                ...state,
                categoriesLoading: false,
                rootPages: uniquePages,
                rootPagination: {
                  ...state.rootPagination,
                  maxReached: !moreEntries,
                }
                };
              });
            },
            error: (err) => {
              console.error('Error loading root pages');
              console.error(err);
            }
          });
        }
      },
      loadPage(pageId: number): void {
        if(store.pagePagination().get(pageId) === undefined) {
          this.createPagePagination(pageId);
        }
        if(store.pagePagination().get(pageId)?.maxReached === false) {
            // Find the last subpage id for pagination
            const lastSubpageId = store.pages().find(p => p.id === pageId)?.subpages.slice(-1)[0]?.id ?? null;

            api.getKnowledgebasePages(
            pageId,
            store.categoryOrder(),
            store.filter.type() === TYPE.KnowledgebaseFilterType.CATEGORY ? store.filter.query() : '',
            store.pagePagination().get(pageId)!.limit ?? 10,
            lastSubpageId
            )
            .pipe(take(1))
            .subscribe({
            next: (response) => {
              const page = response.data;
              const moreEntries = response.moreEntries;
              patchState(store, (state: KnowledgebaseState) => {
              const existingPage = state.pages.find(p => p.id === page.id);

              if (existingPage) {
                // Merge new subpages with existing ones
                const existingSubpageIds = new Set(existingPage.subpages.map(sp => sp.id));
                const newSubpages = page.subpages.filter(sp => !existingSubpageIds.has(sp.id));

                const updatedPage: KnowledgebasePageResource = {
                ...page,
                subpages: [...existingPage.subpages, ...newSubpages]
                };

                return {
                ...state,
                categoriesLoading: false,
                pages: state.pages.map(p => p.id === page.id ? updatedPage : p)
                };
              } else {
                // New page, add it to the list
                return {
                ...state,
                categoriesLoading: false,
                pages: [...state.pages, page]
                };
              }
              });
              // Update pagePagination for the current page
              const currentPagination = store.pagePagination().get(pageId);
              const newMaxReached = !moreEntries;
              const updatedPagePagination = new Map(store.pagePagination());
              updatedPagePagination.set(pageId, {
              ...currentPagination,
              maxReached: newMaxReached,
              limit: currentPagination?.limit ?? 10,
              });

              patchState(store, (state: KnowledgebaseState) => ({
              ...state,
              pagePagination: updatedPagePagination,
              }));
            },
            error: (err) => {
              console.error('Error loading pages for page ID: ', pageId);
              console.error(err);
            }
            });
        }
      },
      loadArticlesForPage(): void {
        if(store.hasParentCategory()){
          if(store.articlesPagination().get(Number(store.routeData.categoryId())) === undefined) {
            this.createArticlePagination(Number(store.routeData.categoryId()));
          }
          if( store.articlesPagination().get(Number(store.routeData.categoryId()))?.maxReached === false) {
            api.getKnowledgebasePageContent(
              store.routeData.categoryId() as number,
              store.categoryContentOrder(),
              store.filter.type() !== TYPE.KnowledgebaseFilterType.CATEGORY ? store.filter.query() :'',
              store.articlesPagination().get(Number(store.routeData.categoryId()))!.limit ?? 50,
              store.articlesPagination().get(Number(store.routeData.categoryId()))!.skip ?? 0
              // store.filter.type() === TYPE.KnowledgebaseFilterType.FILE ? 'files': 'articles'
            ).pipe(take(1))
            .subscribe({
              next: (articles: KnowledgebaseContentListResource[]) => {
                const updatedArticles = new Map(store.articles());
                  const categoryId = Number(store.routeData.categoryId());
                  const existingArticles = updatedArticles.get(categoryId) ?? [];
                  // Create a map of new articles by id for quick lookup
                  const newArticlesMap = new Map(articles.map(a => [a.id, a]));
                  // Filter out existing articles that are being replaced
                  const mergedArticles = [
                  ...existingArticles.filter(a => !newArticlesMap.has(a.id)),
                  ...articles
                  ];
                  updatedArticles.set(categoryId, mergedArticles);
                patchState(store, (state: KnowledgebaseState) => ({
                  ...state,
                  articles: updatedArticles,
                }));
                // Update articlesPagination for the current category
                const currentPagination = store.articlesPagination().get(categoryId);
                const newSkip = (currentPagination?.skip ?? 0) + (currentPagination?.limit ?? 50);
                const newMaxReached = articles.length < (currentPagination?.limit ?? 50);
                const updatedArticlesPagination = new Map(store.articlesPagination());
                updatedArticlesPagination.set(categoryId, {
                  ...currentPagination,
                  skip: newSkip,
                  maxReached: newMaxReached,
                  limit: currentPagination?.limit ?? 50,
                });

                patchState(store, (state: KnowledgebaseState) => ({
                  ...state,
                  articles: updatedArticles,
                  articlesPagination: updatedArticlesPagination,
                }));
              },
              error: (err) => {
                console.error('Error loading articles for page ID: ', store.routeData.categoryId());
                console.error(err);
              }
            });
          }
        }
      },
      loadFilesForPage(): void {
        if(store.hasParentCategory()){
          if(store.filesPagination().get(Number(store.routeData.categoryId())) === undefined) {
            this.createFilePagination(Number(store.routeData.categoryId()));
          }
          if(store.filesPagination().get(Number(store.routeData.categoryId()))?.maxReached === false) {
            api.getKnowledgebasePageContentFiles(
              store.routeData.categoryId() as number,
              store.categoryContentOrder(),
              store.filter.type() !== TYPE.KnowledgebaseFilterType.CATEGORY ? store.filter.query() :'',
              store.filesPagination().get(Number(store.routeData.categoryId()))!.limit ?? 50,
              store.filesPagination().get(Number(store.routeData.categoryId()))!.skip ?? 0
            ).pipe(take(1))
            .subscribe({
              next: (files: FileStorageResource[]) => {
                const updatedFiles = new Map(store.files());
                const categoryId = Number(store.routeData.categoryId());
                const existingFiles = updatedFiles.get(categoryId) ?? [];
                // Create a map of new files by id for quick lookup
                const newFilesMap = new Map(files.map(f => [f.id, f]));
                // Filter out existing files that are being replaced
                const mergedFiles = [
                  ...existingFiles.filter(f => !newFilesMap.has(f.id)),
                  ...files
                ];
                updatedFiles.set(categoryId, mergedFiles);
                patchState(store, (state: KnowledgebaseState) => ({
                  ...state,
                  files: updatedFiles,
                }));
                // Update filesPagination for the current category
                const currentPagination = store.filesPagination().get(categoryId);
                const newSkip = (currentPagination?.skip ?? 0) + (currentPagination?.limit ?? 50);
                const newMaxReached = files.length < (currentPagination?.limit ?? 50);
                const updatedFilesPagination = new Map(store.filesPagination());
                updatedFilesPagination.set(categoryId, {
                  ...currentPagination,
                  skip: newSkip,
                  maxReached: newMaxReached,
                  limit: currentPagination?.limit ?? 50,
                });

                patchState(store, (state: KnowledgebaseState) => ({
                  ...state,
                  files: updatedFiles,
                  filesPagination: updatedFilesPagination,
                }));
              },
              error: (err) => {
                console.error('Error loading files for page ID: ', store.routeData.categoryId());
                console.error(err);
              }
            });
          }
        }
      },
      createArticlePagination(categoryId: number): void {
        patchState(store, (state: KnowledgebaseState) => ({
          ...state,
          articlesPagination: state.articlesPagination.set(categoryId, {
            skip: 0,
            limit: 50,
            maxReached: false,
          })
        }));
      },
      createFilePagination(categoryId: number): void {
        patchState(store, (state: KnowledgebaseState) => ({
          ...state,
          filesPagination: state.filesPagination.set(categoryId, {
            skip: 0,
            limit: 50,
            maxReached: false,
          })
        }));
      },
      createPagePagination(pageId: number): void {
        patchState(store, (state: KnowledgebaseState) => ({
          ...state,
          pagePagination: state.pagePagination.set(pageId, {
            limit: 10,
            maxReached: false,
          })
        }));
      },
      loadSpecificCategory(categoryID: number): Observable<KnowledgebasePageAdminResource | null> {
        return new Observable<KnowledgebasePageAdminResource | null>((observer) => {
          api.getKnowledgebasePageById(categoryID)
          .pipe(take(1))
          .subscribe({
            next: (category: KnowledgebasePageAdminResource) => {
              observer.next(category);
              observer.complete();
            },
            error: (err) => {
              console.error('Error loading specific category for category ID: ', categoryID);
              console.error(err);
              observer.next(null);
              observer.complete();
            }
          });
        });
      },
      loadSpecificArticle(articleID: number): Observable<KnowledgebaseContentResource | null> {
        return new Observable<KnowledgebaseContentResource | null>((observer) => {
          api.getKnowledgebasePageContentById(articleID)
          .pipe(take(1))
          .subscribe({
            next: (category: KnowledgebaseContentResource) => {
              observer.next(category);
              observer.complete();
            },
            error: (err) => {
              console.error('Error loading specific article for article ID: ', articleID);
              console.error(err);
              observer.next(null);
              observer.complete();
            }
          });
        });
      },
      resetCategoryTree(): void {
        patchState(store, (state: KnowledgebaseState) => ({
          ...state,
          categoryTree: {
            entries: [],
            searchString: '',
          }
        }));
      },
      changeCategoryTreeSearchString(searchString: string): void {
        this.resetCategoryTree();
        patchState(store, (state: KnowledgebaseState) => ({
          ...state,
          categoryTree: {
            ...state.categoryTree,
            searchString: searchString
          }
        }));
      },
      loadCategoryTree(): void {
          api.getKnowledgebasePageTree(store.categoryTree().searchString)
          .pipe(take(1))
          .subscribe({
            next: (categoryTree: KnowledgebasePageTreeResource[]) => {
              patchState(store, (state: KnowledgebaseState) => ({
                ...state,
                categoryTree: {
                  ...state.categoryTree,
                  entries: categoryTree
                }
              }));
            },
            error: (err) => { 
              console.error('Error loading category tree with search string: ', store.categoryTree().searchString);
              console.error(err);
            }
          });
      },
      toggleCategoryIsFavorite(data: {categoryId: number, important: boolean}): void {
        api.setKnowledgebasePageFavorite(data.categoryId, data.important)
        .pipe(take(1))
        .subscribe({
          next: () => {
            this.reloadCategories();
          },
          error: () => {
            console.error('Error toggling category favorite status for category ID: ', data);
          }
        });
      },
      deleteCategory(categoryID: number): void {
        api.deleteKnowledgebasePage(categoryID)
        .pipe(take(1))
        .subscribe({
          next: () => {
            this.resetRootPages();
            this.resetPages();
            this.loadCategories();
          },
          error: () => {
            console.error('Error deleting category for category ID: ', categoryID);
          }
        });
      },
      createUpdateCategory(category: KnowledgebasePageUpdateRequest, existingID: number | null): void {
        api.createOrUpdateKnowledgebasePage(category, existingID)
        .pipe(take(1))
        .subscribe({
          next: () => {
            this.resetRootPages();
            this.resetPages();
            this.loadCategories();
          },
          error: (err) => {
            console.error('Error creating/updating category: ', category);
            console.error(err);
          }
        });
      },
      updatePageOrder(pageID: number, direction: KnowledgebaseMoveDirection): void {
        api.updateKnowledgebasePageOrder(pageID, {direction: direction})
        .pipe(take(1))
        .subscribe({
          next: () => {
            this.resetRootPages();
            this.resetPages();
            this.loadCategories();
          },
          error: (err) => {
            console.error('Error updating category order for category ID: ', pageID, ' and direction: ', direction);
            console.error(err);
          }
        });
      },
      createUpdateArticle(article: KnowledgebaseContentUpdateRequest, existingID: number | null): void {
        api.createOrUpdateKnowledgebasePageContent(article, existingID)
        .pipe(take(1))
        .subscribe({
          next: () => {
            this.resetArticles();
            this.resetFiles();
            this.loadArticlesForPage();
            this.loadFilesForPage();
          },
          error: (err) => {
            console.error('Error creating/updating article: ', article);
            console.error(err);
          }
        });
      },
      moveArticleLeft(articleID: number): void {
        api.updateKnowledgebasePageContentOrder(articleID, {direction: KnowledgebaseMoveDirection.Up})
        .pipe(take(1))
        .subscribe({
          next: () => {
            this.resetArticles();
            this.resetFiles();
            this.loadArticlesForPage();
            this.loadFilesForPage();
          },
          error: (err) => {
            console.error('Error moving article left for article ID: ', articleID);
            console.error(err);
          }
        });
      },
      moveArticleRight(articleID: number): void {
        api.updateKnowledgebasePageContentOrder(articleID, {direction: KnowledgebaseMoveDirection.Down})
        .pipe(take(1))
        .subscribe({
          next: () => {
            this.resetArticles();
            this.resetFiles();
            this.loadArticlesForPage();
            this.loadFilesForPage();
          },
          error: (err) => {
            console.error('Error moving article right for article ID: ', articleID);
            console.error(err);
          }
        });
      },
      setArticleFavorite(articleID: number, important: boolean): void {
        api.setKnowledgebasePageContentFavorite(articleID, important)
        .pipe(take(1))
        .subscribe({
          next: () => {
            this.resetArticles();
            this.resetFiles();
            this.loadArticlesForPage();
            this.loadFilesForPage();
          },
          error: (err) => {
            console.error('Error toggling article favorite status for article ID: ', articleID, ' and important: ', important);
            console.error(err);
          }
        });
      },
      deleteArticle(articleID: number): void {
        api.deleteKnowledgebasePageContent(articleID)
        .pipe(take(1))
        .subscribe({
          next: () => {
            this.resetArticles();
            this.resetFiles();
            this.loadArticlesForPage();
            this.loadFilesForPage();
          },
          error: () => {
            console.error('Error deleting article for article ID: ', articleID);
          }
        });
      },
      updateArticleReadCount(articleId: number, readCount: number | null): void {
        const updatedArticles = new Map<number, KnowledgebaseContentListResource[]>();
        store.articles().forEach((articles, categoryId) => {
          const mappedArticles = articles.map((article) =>
            article.id === articleId ? {...article, read_count: readCount} : article
          );
          updatedArticles.set(categoryId, mappedArticles);
        });
        patchState(store, (state: KnowledgebaseState) => ({
          ...state,
          articles: updatedArticles,
        }));
      },
      getArticleReaders(query: string, limit: number, skip: number, articleId: number): Observable<UserMinimalResource[]> {
        return new Observable<UserMinimalResource[]>((observer) => {
          api.getKnowledgebasePageReaders(articleId, query, limit, skip)
          .pipe(take(1))
          .subscribe({
            next: (readers: UserMinimalResource[]) => {
              observer.next(readers);
              observer.complete();
            },
            error: (err) => {
              console.error('Error loading article readers for article ID: ', articleId);
              console.error(err);
              observer.next([]);
              observer.complete();
            }
          });
        });
      },
      moveArticleToCategory(articleID: number, newParentID: number): void {
        this.loadSpecificArticle(articleID)
        .pipe(take(1))
        .subscribe({
          next: (article) => {
            const mappedArticle: KnowledgebaseContentUpdateRequest = {
              parent_id: newParentID,
              title: article?.title || '',
              content: article?.content || '',
              notify_important: article?.favorite || false,
              timetable_start: article?.timetable_start || null,
              timetable_dead: article?.timetable_dead || null,
              file_storage_ids: article?.files.map((x) => x.id) || [],
              notify_users: false, // No need to notify users when just moving an article, as there are no content changes
            };
            this.createUpdateArticle(mappedArticle, articleID);
          },
          error: (err) => {
            console.error('Error loading article for moving article ID: ', articleID, ' to category ID: ', newParentID);
            console.error(err);
          }
        });
      },
      moveCategory(categoryID: number, newParentID: number): void {
        this.loadSpecificCategory(categoryID)
        .pipe(take(1))
        .subscribe({
          next: (category) => {
            const mappedCategory: KnowledgebasePageUpdateRequest = {
              name: category?.name || '',
              description: category?.description || '',
              parent_id: newParentID,
              hide_editor_icon: category?.hide_editor_icon || false,
              link: category?.link || null,
              file_storage_id: category?.image?.id || null,
              editor_users: category?.editor_users || [],
              editor_groups: category?.editor_groups || [],
              viewer_groups: category?.viewer_groups || [],
            };
            this.createUpdateCategory(mappedCategory, categoryID);
          },
          error: (err) => {
            console.error('Error loading category for moving category ID: ', categoryID, ' to category ID: ', newParentID);
            console.error(err);
          }
        });
      },
      exportArticles():void {
        api.exportKnowledgebasePage(store.routeData.categoryId() as number)
        .pipe(take(1))
        .subscribe({
          next: (data: string | ArrayBuffer) => {
            const filename = 'HMSapp_Page_' + (store.routeData.categoryId() ?? 'unknown') + '_export.val'; // You can parse from content-disposition if needed
            documentService.downloadData(data, filename, 'application/gzip');
          },
          error: (err) => {            
            console.error('Error exporting articles for category ID: ', store.routeData.categoryId());
            console.error(err);
          } 
        });
      },
      importArticles(document: FileStorageResource): void {
        api.importKnowledgebasePage(store.routeData.categoryId() ?? 0, document.id!)
        .pipe(take(1))
        .subscribe({
          next: () => {
            this.resetRootPages();
            this.resetPages();
            this.resetArticles();
            this.resetFiles();
            this.loadArticlesForPage();
            this.loadFilesForPage();
            this.loadCategories();
          },
          error: (err) => {
            console.error('Error importing articles from document: ', document);
            console.error(err);
          }
        });
      },
      resetRootPages(): void {
        patchState(store, (state: KnowledgebaseState) => ({
          ...state,
          rootPages: initialState.rootPages,
          rootPagination: initialState.rootPagination,
        }));
      },
      resetPages(): void {
        patchState(store, (state: KnowledgebaseState) => ({
          ...state,
          pages: initialState.pages,
          pagePagination: initialState.pagePagination,
        }));
      },
      resetArticles(): void {
        patchState(store, (state: KnowledgebaseState) => ({
          ...state,
          articles: initialState.articles,
          articlesPagination: initialState.articlesPagination,
        }));
      },
      resetFiles(): void {
        patchState(store, (state: KnowledgebaseState) => ({
          ...state,
          files: initialState.files,
          filesPagination: initialState.filesPagination,
        }));
      },
      natigateToCategory(categoryId: number): void {
        const subpage = store.currentCategory()?.subpages.find(sub => sub.id === categoryId);
        if(subpage) {
          if(subpage.link !== null) {
            window.open(subpage.link, '_blank');
          } else {
            router.navigate(['/knowledgebase', categoryId]);
            this.resetCategoryContentSettings();
          }
        } else {
            router.navigate(['/knowledgebase', categoryId]);
            this.resetCategoryContentSettings();
        }
      },
      openFile(file: FileStorageResource): void {
        documentService.downloadDocument(file);
      }
  }
  }),
  withComputed((store) => ({
    currentSubpages: computed((): KnowledgebaseSubPageResource[] => {
      const idToCompare = (store.routeData.categoryId() === null || store.routeData.categoryId() === 0) ? null : store.routeData.categoryId();
      if(idToCompare === null) {
        return store.rootPages();
      } else {
        return store.pages().find((page) => page.id === idToCompare)?.subpages ?? [];
      }
    })
  })),
)
